<script setup lang="ts">
/**
 * Sign in, register, or ask for a password reset: one steel card, three modes.
 *
 * It is `AuthShell`'s riveted card over the blurred dusk painting. Sign in and
 * Register are the two kit folder tabs; each entry is a dark inset well with a
 * painted icon (`letter`, `lock`, `cap`); submit is the one big yellow plate;
 * back and "forgot" are small quiet plates under the card. The welcome moment
 * is a gold ribbon (`EnlistedRibbon`) struck across the card. Forgot-password
 * is its own component (`auth/ForgotPasswordForm.vue`).
 *
 * The template's auth requires a solved ALTCHA payload on register and login
 * alike, so each form carries its widget; a refused submit re-runs it, because
 * a payload is single-use server-side (`auth/CaptchaWell.vue`).
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { reducedMotion } from '@/engine/motion';
import { playCue } from '@/audio/sfx';
import { useI18n } from '@/i18n';
import StampSlam from './motion/StampSlam.vue';

import { useSession } from '@/state/session';
import AuthShell from './AuthShell.vue';
import AuthField from './auth/AuthField.vue';
import AuthNotice from './auth/AuthNotice.vue';
import CaptchaWell from './auth/CaptchaWell.vue';
import EnlistedRibbon from './auth/EnlistedRibbon.vue';
import ForgotPasswordForm from './auth/ForgotPasswordForm.vue';
import PasswordField from './PasswordField.vue';
import LegalOverlay from './LegalOverlay.vue';
import ActionButton from './panel/ActionButton.vue';
import Check from './panel/Check.vue';
import { canSubmitJoin, captchaCapable, joinBlockReason, shouldShowBlock, type JoinGateInput } from './join-gate';

const props = withDefaults(defineProps<{ initialMode?: 'login' | 'register' }>(), { initialMode: 'login' });
const emit = defineEmits<{ back: [] }>();

const session = useSession();
const { t } = useI18n();

/**
 * The seeded dev captain, filled in for a local run only.
 *
 * `import.meta.env.DEV` is replaced at build time, so a production bundle
 * carries the empty string and not the address. The template's own login
 * screen does the same (`core/auth/login-screen.ts`). `prisma/seed.ts` also
 * makes `player1@tactus.dev` and `player2@tactus.dev` on the same password.
 */
const DEV_EMAIL = import.meta.env.DEV ? 'admin@tactus.dev' : '';
const DEV_PASSWORD = import.meta.env.DEV ? 'tactus123' : '';

const NICKNAME_MAX = 16;

const mode = ref<'login' | 'register' | 'forgot'>(props.initialMode);
const nickname = ref('');
const email = ref(DEV_EMAIL);
const password = ref(DEV_PASSWORD);
const repeatPassword = ref('');
const agreedToTerms = ref(false);
const legalSlug = ref<string | null>(null);

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && legalSlug.value) legalSlug.value = null;
}
onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));

const altchaPayload = ref('');
const captcha = ref<InstanceType<typeof CaptchaWell> | null>(null);

const isRegister = computed(() => mode.value === 'register');
const isForgot = computed(() => mode.value === 'forgot');

const canCaptcha = captchaCapable();

const gate = computed<JoinGateInput>(() => ({
  register: isRegister.value,
  nickname: nickname.value,
  email: email.value,
  password: password.value,
  repeatPassword: repeatPassword.value,
  agreedToTerms: agreedToTerms.value,
  captchaSolved: altchaPayload.value !== '',
  captchaCapable: canCaptcha,
  busy: session.busy.value,
}));

const canSubmit = computed(() => canSubmitJoin(gate.value));
const block = computed(() => {
  const reason = joinBlockReason(gate.value);
  return shouldShowBlock(reason, gate.value) ? reason : null;
});

watch(mode, (next) => {
  session.clearError();
  // Back to the sign-in tab on a dev run, hand the seeded captain back; on the
  // register tab clear them, or the form would arrive pre-filled with an
  // address that is already taken.
  password.value = next === 'login' ? DEV_PASSWORD : '';
  repeatPassword.value = '';
  if (next === 'register' && email.value === DEV_EMAIL) email.value = '';
  else if (next === 'login' && email.value === '') email.value = DEV_EMAIL;
});

// ─── The welcome ribbon ──────────────────────────────────────────────────────

/** How long the ribbon is held before the harbour takes over (life-layer.md brief, item 3: about 450 ms). */
const ENLIST_HOLD_MS = 450;
const enlisted = ref(false);

/** The server said yes: strike the ribbon across the card and let it be seen, then hand over. */
async function enlist(): Promise<void> {
  enlisted.value = true;
  playCue('ui.open');
  if (!reducedMotion()) await new Promise((resolve) => setTimeout(resolve, ENLIST_HOLD_MS));
}

/** An error shakes the form once; the line itself types in. */
const shaking = ref(false);
const SHAKE_MS = 360;
watch(
  () => session.error.value,
  (message) => {
    if (!message) return;
    shaking.value = false;
    requestAnimationFrame(() => (shaking.value = true));
    setTimeout(() => (shaking.value = false), SHAKE_MS);
  },
);

async function submit(): Promise<void> {
  if (!canSubmit.value) return;

  const ok = isRegister.value
    ? await session.register({
        nickname: nickname.value.trim(),
        email: email.value.trim(),
        password: password.value,
        altcha: altchaPayload.value,
      }, enlist)
    : await session.login({
        email: email.value.trim(),
        password: password.value,
        altcha: altchaPayload.value,
      }, enlist);

  if (!ok) captcha.value?.rerun();
}

function pickMode(next: 'login' | 'register'): void {
  playCue('ui.tab');
  mode.value = next;
}
</script>

<template>
  <AuthShell
    :locked="session.busy.value"
    test-id="join-screen"
    :title="isForgot ? t('join.forgotTitle') : undefined"
    :icon="isForgot ? 'lock' : 'anchor'"
  >
    <template v-if="!isForgot" #tabs>
      <!-- The kit's primary/secondary pair, as on the landing: the open mode is yellow, the other navy. -->
      <ActionButton
        role="tab"
        :variant="isRegister ? 'quiet' : 'primary'"
        block
        data-test="tab-login"
        :aria-selected="!isRegister"
        @click="pickMode('login')"
      >
        {{ t('join.signIn') }}
      </ActionButton>
      <ActionButton
        role="tab"
        :variant="isRegister ? 'primary' : 'quiet'"
        block
        data-test="tab-register"
        :aria-selected="isRegister"
        @click="pickMode('register')"
      >
        {{ t('join.register') }}
      </ActionButton>
    </template>

    <StampSlam>
      <EnlistedRibbon v-if="enlisted" data-test="enlisted" role="status">
        {{ isRegister ? t('join.enlisted') : t('join.welcome') }}
      </EnlistedRibbon>
    </StampSlam>

    <template v-if="!isForgot">
      <form class="join-form" :class="{ shaking }" @submit.prevent="submit">
        <div class="fields">
          <AuthField
            v-if="isRegister"
            id="nickname"
            v-model="nickname"
            autocomplete="username"
            pattern="[A-Za-z0-9_\-]+"
            :maxlength="NICKNAME_MAX"
            :label="t('join.nickname')"
            :placeholder="t('join.nicknamePlaceholder')"
            :disabled="session.busy.value"
          />

          <AuthField
            id="email"
            v-model="email"
            type="email"
            autocomplete="email"
            :label="t('join.email')"
            :placeholder="t('join.email')"
            :disabled="session.busy.value"
          />

          <PasswordField
            id="password"
            v-model="password"
            label-hidden
            :label="t('join.password')"
            :autocomplete="isRegister ? 'new-password' : 'current-password'"
            :placeholder="isRegister ? t('join.passwordNewPlaceholder') : t('join.password')"
            :disabled="session.busy.value"
            data-test="password"
          />

          <PasswordField
            v-if="isRegister"
            id="repeat-password"
            v-model="repeatPassword"
            label-hidden
            autocomplete="new-password"
            :label="t('join.repeatPassword')"
            :placeholder="t('join.repeatPassword')"
            :disabled="session.busy.value"
            data-test="repeat-password"
          />
        </div>

        <div class="actions">
          <!-- One line, not a well of plates: the documents are links inside the sentence. -->
          <div v-if="isRegister" class="terms">
            <Check
              id="agree-terms"
              v-model="agreedToTerms"
              :disabled="session.busy.value"
              data-test="agree-terms"
              :aria-label="t('join.agree')"
            >
              {{ t('join.agreeLead') }}
            </Check>
            <button type="button" class="link" @click="legalSlug = 'terms-of-service'">{{ t('join.terms') }}</button>
            <span class="and">{{ t('join.agreeAnd') }}</span>
            <button type="button" class="link" @click="legalSlug = 'privacy-policy'">{{ t('join.privacy') }}</button>
          </div>

          <CaptchaWell ref="captcha" v-model="altchaPayload" test-id="altcha" />

          <AuthNotice v-if="block" data-test="join-block">{{ block.message }}</AuthNotice>

          <ActionButton
            type="submit"
            variant="primary"
            block
            size="lg"
            data-test="submit"
            :disabled="!canSubmit"
            :loading="session.busy.value"
          >
            {{ session.busy.value ? t('auth.working') : isRegister ? t('join.createAccount') : t('join.signIn') }}
          </ActionButton>

          <AuthNotice v-if="session.error.value" :key="session.error.value" tone="error" data-test="auth-error">
            {{ session.error.value }}
          </AuthNotice>
        </div>
      </form>
    </template>

    <ForgotPasswordForm v-else @back="mode = 'login'" />

    <template #after>
      <button v-if="!isForgot" type="button" class="link" data-test="back-to-landing" @click="emit('back')">{{ t('join.back') }}</button>
      <button v-if="mode === 'login'" type="button" class="link" data-test="forgot" @click="mode = 'forgot'">
        {{ t('join.forgot') }}
      </button>
    </template>

    <LegalOverlay :slug="legalSlug" @close="legalSlug = null" />
  </AuthShell>
</template>

<style scoped>
.join-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fields,
.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* The terms tick and its sentence on one line; the two documents are links in it. */
.terms {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 2px;
  min-height: 44px;
  padding: 0 4px;
  font-family: var(--font-game-body);
  font-size: 0.9rem;
  color: #c9d4de;
}

.terms :deep(.text) {
  color: #c9d4de;
  font-family: var(--font-game-body);
  font-weight: 500;
}

/* A text link: secondary actions (documents, back, forgot) are words, not more plates. */
.link {
  min-height: 44px;
  padding: 0 4px;
  border: 0;
  background: none;
  color: #e2c88e;
  font-family: var(--font-game-body);
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(226, 200, 142, 0.45);
  text-underline-offset: 3px;
  cursor: pointer;
  touch-action: manipulation;
}

@media (hover: hover) {
  .link:hover {
    color: #f3e2b8;
  }
}

/* The error shakes the form: transform only (keyframes `shake` live in style.css). */
form.shaking {
  animation: shake 0.32s;
}

/* A short landscape phone: fields on the left, captcha and submit on the right. */
@media (max-height: 520px) and (min-width: 640px) {
  .join-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: start;
    gap: 14px;
  }

  .fields,
  .actions {
    gap: 8px;
  }
}
</style>
